import { useUpdateCandidate } from "@vidario/api-client";
import { type Candidate } from "@vidario/domain";
import { Button, Card, HiddenBlock, Markdown, Textarea, cn, useToast } from "@vidario/ui";
import { Lock, Pencil } from "lucide-react";
import { useState, type ReactElement } from "react";

import { useAnonymView } from "../../anonym/useAnonymView";
import { HIDDEN_NOTES_TEXT } from "../../anonym/copy";

export interface NotesCardProps {
  jobId: string;
  candidate: Candidate;
  isAdmin: boolean;
}

type TabId = "recruiter" | "internal";

/** Notes block (MVP §4.6 + §4.6a). Recruiter + admin see the recruiter
 * tab; admin also sees the internal tab. Each tab follows the
 * view-until-click pattern: the saved notes render as Markdown; clicking
 * "Bearbeiten" swaps in a Textarea with Save / Abbrechen. Markdown is
 * sanitized at render time (see @vidario/ui/Markdown). */
export function NotesCard({ jobId, candidate, isAdmin }: NotesCardProps): ReactElement {
  const [tab, setTab] = useState<TabId>("recruiter");
  const [editingRecruiter, setEditingRecruiter] = useState(false);
  const [editingInternal, setEditingInternal] = useState(false);
  const [draftRecruiter, setDraftRecruiter] = useState(candidate.recruiterNotes ?? "");
  const [draftInternal, setDraftInternal] = useState(candidate.internalNotes ?? "");
  const update = useUpdateCandidate();
  const toast = useToast();
  const anonym = useAnonymView();

  // NotesCard is mounted with `key={candidate.id}` at the caller so
  // switching candidates re-initialises drafts — no reset-in-effect.

  const save = (): void => {
    const patch =
      tab === "recruiter"
        ? { recruiterNotes: draftRecruiter || null }
        : { internalNotes: draftInternal || null };
    update.mutate(
      { jobId, candidateId: candidate.id, patch },
      {
        onSuccess: () => {
          if (tab === "recruiter") setEditingRecruiter(false);
          else setEditingInternal(false);
          toast.push({
            kind: "success",
            title: "Notiz gespeichert",
            message:
              tab === "recruiter"
                ? "Für dich und das Vidario-Team sichtbar."
                : "Intern – nicht für Kunden sichtbar.",
          });
        },
        onError: (err) => toast.push({ kind: "error", title: "Fehler", message: err.message }),
      },
    );
  };

  const cancel = (): void => {
    if (tab === "recruiter") {
      setDraftRecruiter(candidate.recruiterNotes ?? "");
      setEditingRecruiter(false);
    } else {
      setDraftInternal(candidate.internalNotes ?? "");
      setEditingInternal(false);
    }
  };

  const activeEditing = tab === "recruiter" ? editingRecruiter : editingInternal;
  const activeValue = tab === "recruiter" ? candidate.recruiterNotes : candidate.internalNotes;
  const activeDraft = tab === "recruiter" ? draftRecruiter : draftInternal;
  const setActiveDraft = tab === "recruiter" ? setDraftRecruiter : setDraftInternal;
  const beginEdit = (): void => {
    if (tab === "recruiter") setEditingRecruiter(true);
    else setEditingInternal(true);
  };

  if (anonym) {
    // Drafts stay in state and come back when the Anonym-Ansicht is off (#140).
    return (
      <Card>
        <h3 className="m-0 mb-3 text-[15px] font-semibold">Notizen</h3>
        <HiddenBlock>{HIDDEN_NOTES_TEXT}</HiddenBlock>
      </Card>
    );
  }

  return (
    <Card>
      <div className="-mx-1 -mt-1 mb-4 flex flex-wrap items-center justify-between gap-x-3 border-b border-border-default">
        <div className="flex min-w-0 flex-wrap gap-0.5">
          <TabBtn
            label="Meine Notizen"
            active={tab === "recruiter"}
            onClick={() => setTab("recruiter")}
          />
          {isAdmin && (
            <TabBtn
              label="Vidario-intern"
              admin
              active={tab === "internal"}
              onClick={() => setTab("internal")}
            />
          )}
        </div>
        {!activeEditing && (
          <button
            type="button"
            onClick={beginEdit}
            className="mb-2 ml-auto inline-flex items-center gap-1 text-[12px] font-medium text-primary-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <Pencil size={12} /> Bearbeiten
          </button>
        )}
      </div>

      {activeEditing ? (
        <>
          <Textarea
            aria-label={tab === "recruiter" ? "Eigene Notizen" : "Vidario-interne Notizen"}
            rows={5}
            placeholder={
              tab === "recruiter"
                ? "Notiz hinzufügen… (Markdown wird unterstützt)"
                : "Interne Notizen — nicht für Kunden sichtbar… (Markdown)"
            }
            value={activeDraft}
            onChange={(e) => setActiveDraft(e.target.value)}
          />
          <div className="mt-2 flex items-center justify-between">
            <span className="text-xs text-text-tertiary">
              **fett** · *kursiv* · - Liste · [Link](https://…)
            </span>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={cancel}>
                Abbrechen
              </Button>
              <Button size="sm" loading={update.isPending} onClick={save}>
                Speichern
              </Button>
            </div>
          </div>
        </>
      ) : (
        <Markdown empty={tab === "recruiter" ? "Noch keine Notiz." : "Keine interne Notiz."}>
          {activeValue ?? ""}
        </Markdown>
      )}
    </Card>
  );
}

function TabBtn({
  label,
  active,
  admin,
  onClick,
}: {
  label: string;
  active: boolean;
  admin?: boolean;
  onClick: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={cn(
        "-mb-px inline-flex items-center gap-1.5 border-b-2 px-3.5 py-2.5 text-[13px] transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
        active
          ? admin
            ? "border-[var(--admin-marker)] font-semibold text-[color:var(--admin-marker)]"
            : "border-primary-500 font-semibold text-primary-700"
          : "border-transparent font-medium text-neutral-500 hover:text-neutral-800",
      )}
    >
      {admin && <Lock size={11} />}
      {label}
    </button>
  );
}
